import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import DataTable from "@/components/DataTable";
import { Plus, Clock, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const APPROVERS = ["super_admin", "hr", "manager"];

export default function Overtime() {
  const { user } = useAuth();
  const isApprover = APPROVERS.includes(user?.role);

  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), hours: 2, reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.date) { toast.error("Date required"); return; }
    if (!form.hours || Number(form.hours) <= 0) { toast.error("Hours must be > 0"); return; }
    if (form.reason.trim().length < 3) { toast.error("Reason required"); return; }
    setSubmitting(true);
    try {
      await api.post("/ot/apply", { ...form, hours: Number(form.hours) });
      toast.success("OT request submitted");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0,10), hours: 2, reason: "" });
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const decide = async (id, action) => {
    try {
      await api.post(`/ot/${id}/${action}`, { comment: "" });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="ot-page">
      <PageHeader
        title="Over Time"
        subtitle={isApprover ? "Review and approve OT requests from your team." : "Apply for over-time hours. Your manager / HR will approve."}
        action={
          <Button data-testid="apply-ot" onClick={() => setOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
            <Plus className="h-4 w-4 mr-1.5" /> Apply OT
          </Button>
        }
      />

      <DataTable
        testId="ot-table"
        endpoint="/ot"
        refreshKey={refresh}
        columns={[
          { key: "employee_name", label: "Employee", render: (r) => (
            <div>
              <div className="font-medium">{r.employee_name}</div>
              <div className="text-[11px] text-gray-500 font-mono">{r.employee_id}</div>
            </div>
          )},
          { key: "date", label: "Date" },
          { key: "hours", label: "Hours", render: (r) => <span className="font-mono">{r.hours}</span> },
          { key: "reason", label: "Reason" },
          { key: "status", label: "Status", badge: true },
          ...(isApprover ? [{
            key: "actions",
            label: "Action",
            render: (r) => r.status === "pending" ? (
              <div className="flex gap-1">
                <Button data-testid={`ot-approve-${r.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); decide(r.id, "approve"); }}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Approve
                </Button>
                <Button data-testid={`ot-reject-${r.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); decide(r.id, "reject"); }}>
                  <XCircle className="h-3.5 w-3.5 mr-1 text-rose-600" /> Reject
                </Button>
              </div>
            ) : <span className="text-[11px] text-gray-400">—</span>,
          }] : []),
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="apply-ot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#F26B3A]" /> Apply for OT</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Date</Label>
              <Input data-testid="ot-date" type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Hours</Label>
              <Input data-testid="ot-hours" type="number" step="0.5" min="0.5" max="24" className="mt-1.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
              <Textarea rows={3} data-testid="ot-reason" className="mt-1.5" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Production deployment over the weekend" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="ot-submit" onClick={submit} disabled={submitting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
